import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert, Button, CircularProgress, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, MenuItem, OutlinedInput, Select, Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyApiFieldErrors } from '../../../shared/api';
import AppDialog from '../../../shared/components/AppDialog';
import FileDropField from '../../../shared/components/FileDropField';
import LabeledField from '../../../shared/components/LabeledField';
import {
  CATEGORIES_BY_SCOPE,
  CATEGORY_META,
  SCOPE, SCOPE_SELECT_OPTIONS,
  SUB_CATEGORIES_BY_CATEGORY, SUB_CATEGORY_LABELS_BY_CATEGORY,
} from '../../../shared/constants';
import {
  SCOPE_2_FACTOR_UNIT
} from '../../office/model/scope2Assignment';
import {
  toEmissionFactorForm,
  toEmissionFactorPayload,
} from '../model/emissionFactorForm';

const categoryOptionsFor = (scope) => (CATEGORIES_BY_SCOPE[scope] || []).map((value) => {
  const metadata = CATEGORY_META[value];
  const isCategoryOne = /^CAT_1_[1-9]$/.test(value);
  return {
    value,
    label: isCategoryOne
      ? (metadata?.short || value)
      : `${metadata?.short || value} · ${metadata?.label || value}`,
  };
});

export default function EmissionFactorFormDialog({
  open,
  factor,
  suppliers = [],
  supplierLoadError = null,
  saving,
  onClose,
  onSubmit,
  onStatusChange,
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(factor);
  const [sourceFile, setSourceFile] = useState(null);
  const [statusValue, setStatusValue] = useState('ACTIVE');
  const [statusReason, setStatusReason] = useState('');
  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
    defaultValues: toEmissionFactorForm(factor, suppliers),
  });

  useEffect(() => {
    if (open) {
      reset(toEmissionFactorForm(factor, suppliers));
      setSourceFile(null);
      setStatusValue(factor?.is_active === false ? 'INACTIVE' : 'ACTIVE');
      setStatusReason('');
    }
  }, [factor, open, reset, suppliers]);

  const scope = watch('scope');
  const category = watch('category');
  const subCategory = watch('sub_category');
  const supplierId = watch('supplier_id');
  const catalogSupplier = suppliers.find(supplier => supplier.id === supplierId);
  const selectedSupplier = useMemo(() => catalogSupplier || (supplierId ? {
    id: supplierId,
    registrationNo: factor?.registration_no || factor?.ef_external_id || '',
    nameJa: factor?.source_name_ja || factor?.business_name || factor?.ef_name || '',
  } : null), [catalogSupplier, factor, supplierId]);
  const supplierCatalogUnavailable = Boolean(supplierLoadError) || suppliers.length === 0;
  const categoryOptions = useMemo(() => categoryOptionsFor(scope), [scope]);
  const subCategoryOptions = useMemo(() => SUB_CATEGORIES_BY_CATEGORY[category] || [], [category]);

  useEffect(() => {
    if (!categoryOptions.some((option) => option.value === category)) {
      setValue('category', categoryOptions[0]?.value || '');
      setValue('sub_category', '');
    }
  }, [category, categoryOptions, setValue]);

  useEffect(() => {
    if (subCategory && !subCategoryOptions.includes(subCategory)) {
      setValue('sub_category', '');
    }
  }, [setValue, subCategory, subCategoryOptions]);

  useEffect(() => {
    if (scope !== SCOPE.SCOPE_2) return;
    setValue('category', 'ELECTRICITY');
    setValue('sub_category', '');
    setValue('ef_unit', SCOPE_2_FACTOR_UNIT);
    setValue('ef_name', selectedSupplier?.nameJa || selectedSupplier?.nameEn || '');
    setValue('ef_external_id', selectedSupplier?.registrationNo || '');
    setValue('ef_company_core', '');
  }, [scope, selectedSupplier, setValue]);

  const requiredRule = { required: t('common.fieldRequired') };
  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit({
        ...toEmissionFactorPayload(values),
        source_file: sourceFile,
      });
    } catch (error) {
      if (!applyApiFieldErrors(error, setError, t)) throw error;
    }
  });

  return (
    <AppDialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {t(isEdit ? 'emissionFactor.form.title' : 'emissionFactor.form.addTitle')}
        {factor && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {factor.item_key} · {factor.fiscal_year}
          </Typography>
        )}
        <IconButton
          onClick={onClose}
          disabled={saving}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {errors.root?.server?.message && (
          <Alert severity="error" sx={{ mb: 2 }}>{errors.root.server.message}</Alert>
        )}
        <Grid
          container
          spacing={2.5}
          component="form"
          id="emission-factor-form"
          noValidate
          onSubmit={submit}
        >
          <Grid item xs={12} md={6}>
            <Controller
              name="scope"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField label={t('emissionFactor.field.scope')} htmlFor="ef_scope" required error={errors.scope?.message}>
                  <Select {...field} id="ef_scope" size="small" disabled={isEdit}>
                    {SCOPE_SELECT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </LabeledField>
              )}
            />
          </Grid>
          {factor && (
            <>
              <Grid item xs={12} md={6}>
                <LabeledField label={t('emissionFactor.status.current')} htmlFor="ef_status">
                  <Select
                    id="ef_status"
                    size="small"
                    fullWidth
                    value={statusValue}
                    onChange={(event) => setStatusValue(event.target.value)}
                  >
                    <MenuItem value="ACTIVE">{t('emissionFactor.status.active')}</MenuItem>
                    <MenuItem value="INACTIVE">{t('emissionFactor.status.inactive')}</MenuItem>
                  </Select>
                </LabeledField>
              </Grid>
              <Grid item xs={12} md={6}>
                <LabeledField label={t('emissionFactor.status.reason')} htmlFor="ef_status_reason">
                  <OutlinedInput
                    id="ef_status_reason"
                    size="small"
                    fullWidth
                    value={statusReason}
                    onChange={(event) => setStatusReason(event.target.value)}
                    placeholder={t('emissionFactor.status.reasonPlaceholder')}
                    inputProps={{ maxLength: 500 }}
                  />
                </LabeledField>
              </Grid>
            </>
          )}
          <Grid item xs={12} md={6}>
            <Controller
              name="category"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField label={t('emissionFactor.field.category')} htmlFor="ef_category" required error={errors.category?.message}>
                  <Select
                    {...field}
                    id="ef_category"
                    size="small"
                    disabled={isEdit || scope === SCOPE.SCOPE_2}
                  >
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </LabeledField>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="sub_category"
              control={control}
              rules={subCategoryOptions.length > 0 ? requiredRule : undefined}
              render={({ field }) => (
                <LabeledField
                  label={t('emissionFactor.field.subCategory')}
                  htmlFor="ef_sub_category"
                  required={subCategoryOptions.length > 0}
                  error={errors.sub_category?.message}
                >
                  <Select
                    {...field}
                    id="ef_sub_category"
                    size="small"
                    disabled={isEdit || scope === SCOPE.SCOPE_2 || subCategoryOptions.length === 0}
                  >
                    <MenuItem value="" disabled={subCategoryOptions.length > 0}>{t('common.none')}</MenuItem>
                    {subCategoryOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {SUB_CATEGORY_LABELS_BY_CATEGORY[category]?.[option] || option}
                      </MenuItem>
                    ))}
                  </Select>
                </LabeledField>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="fiscal_year"
              control={control}
              rules={{
                required: t('common.fieldRequired'),
                validate: (value) => (
                  (/^\d{4}$/.test(value) && Number(value) >= 2000 && Number(value) <= 2100)
                  || t('emissionFactor.field.fiscalYearInvalid')
                ),
              }}
              render={({ field }) => (
                <LabeledField label={t('emissionFactor.field.fiscalYear')} htmlFor="ef_fiscal_year" required error={errors.fiscal_year?.message}>
                  <OutlinedInput {...field} id="ef_fiscal_year" size="small" disabled={isEdit} inputProps={{ inputMode: 'numeric' }} />
                </LabeledField>
              )}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Controller
              name="ef_name"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField label={t('emissionFactor.field.efName')} htmlFor="ef_name" required error={errors.ef_name?.message}>
                  <OutlinedInput
                    {...field}
                    id="ef_name"
                    size="small"
                    disabled={scope === SCOPE.SCOPE_2}
                  />
                </LabeledField>
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="ef_external_id"
              control={control}
              render={({ field }) => (
                <LabeledField label={t('emissionFactor.field.efExternalId')} htmlFor="ef_external_id" error={errors.ef_external_id?.message}>
                  <OutlinedInput
                    {...field}
                    id="ef_external_id"
                    size="small"
                    disabled={scope === SCOPE.SCOPE_2}
                  />
                </LabeledField>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="ef_value"
              control={control}
              rules={{
                required: t('common.fieldRequired'),
                validate: (value) => {
                  const decimal = String(value || '').trim();
                  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(decimal)) {
                    return t('emissionFactor.field.efValueInvalid');
                  }
                  const significand = decimal.split(/[eE]/)[0].replace('.', '');
                  if (scope === SCOPE.SCOPE_2 && !/[1-9]/.test(significand)) {
                    return t('emissionFactor.field.scope2Positive');
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <LabeledField label={t('emissionFactor.field.efValue')} htmlFor="ef_value" required error={errors.ef_value?.message}>
                  <OutlinedInput {...field} id="ef_value" size="small" inputProps={{ inputMode: 'decimal' }} />
                </LabeledField>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="ef_unit"
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <LabeledField label={t('emissionFactor.field.efUnit')} htmlFor="ef_unit" required error={errors.ef_unit?.message}>
                  <Select {...field} id="ef_unit" size="small" disabled={scope === SCOPE.SCOPE_2}>
                    {['tCO2/kl', 'tCO2eq/百万円', 'kgCO2eq/kWh', 'tCO2/t', 'kgCO2/JPY', 'kgCO2/泊', 'tCO2/m2・年', 'tCO2/kWh'].map(unit => (
                      <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                    ))}
                  </Select>
                </LabeledField>
              )}
            />
          </Grid>
          {scope === SCOPE.SCOPE_2 && (
            <>
              <Grid item xs={12} md={6}>
                <Controller
                  name="supplier_id"
                  control={control}
                  rules={requiredRule}
                  render={({ field }) => (
                    <LabeledField
                      label={t('emissionFactor.field.supplier')}
                      htmlFor="ef_supplier_id"
                      required
                      error={errors.supplier_id?.message}
                    >
                      <Select
                        {...field}
                        id="ef_supplier_id"
                        size="small"
                        disabled={supplierCatalogUnavailable}
                      >
                        {selectedSupplier && !catalogSupplier && (
                          <MenuItem value={selectedSupplier.id} disabled>
                            {selectedSupplier.registrationNo || '—'} · {selectedSupplier.nameJa
                              || t('emissionFactor.form.supplierUnavailable')}
                          </MenuItem>
                        )}
                        {suppliers.map(supplier => (
                          <MenuItem key={supplier.id} value={supplier.id}>
                            {supplier.registrationNo} · {supplier.nameJa || supplier.nameEn}
                          </MenuItem>
                        ))}
                      </Select>
                      {supplierCatalogUnavailable && (
                        <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
                          {t('emissionFactor.form.supplierCatalogLoadFailed')}
                        </Typography>
                      )}
                    </LabeledField>
                  )}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <LabeledField label={t('emissionFactor.field.sourceFile')} htmlFor="ef_source_file" required>
              {/* Backend source registration accepts only OOXML workbooks up to 25 MB. */}
              <FileDropField
                file={sourceFile}
                currentName={factor?.source_file?.name}
                currentMeta={factor?.source_file?.version_label || ''}
                onChange={setSourceFile}
                accept=".xlsx"
                maxSizeMb={25}
              />
            </LabeledField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
        {factor && statusValue !== (factor.is_active ? 'ACTIVE' : 'INACTIVE') && (
          <Button
            color={statusValue === 'INACTIVE' ? 'warning' : 'success'}
            onClick={() => onStatusChange(
              factor,
              statusValue,
              statusReason.trim(),
            )}
            disabled={saving || !statusReason.trim()}
          >
            {statusValue === 'INACTIVE'
              ? t('emissionFactor.status.deactivate')
              : t('emissionFactor.status.activate')}
          </Button>
        )}
        <Button
          type="submit"
          form="emission-factor-form"
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress color="inherit" size={15} /> : undefined}
        >
          {t(isEdit
            ? (saving ? 'emissionFactor.form.updating' : 'emissionFactor.form.update')
            : (saving ? 'emissionFactor.form.creating' : 'emissionFactor.form.create'))}
        </Button>
      </DialogActions>
    </AppDialog>
  );
}
